import {Button, Menu} from 'antd';
import Spin from 'antd/es/spin';
import React, {useRef, useState, FC, Key, useEffect} from 'react';

import {ActionBar} from 'components/@common/widgets/action-bar';
import {CustomCard} from 'components/@common/widgets/custom-card';
import {KebabMenu} from 'components/@common/widgets/kebab-menu';
import {ParametersFilter} from 'components/@common/widgets/parameters-filter';
import {IParametersFilterRef} from 'components/@common/widgets/parameters-filter/parameters-filter';
import {ModalOpenerComponent} from 'components/modal-opener-component';
import {TableLaunchProgramModalAction} from 'components/table/action';
import {CONTAINER_SELECTORS} from 'features/horizontal-scroll/model/constants';
import {HorizontalScroll} from 'features/horizontal-scroll/ui/horizontal-scroll';
import {selectContextRawData} from 'modules/context/context-selector';
import {ReactComponent as ReloadOutlined} from 'shared/assets/reload.svg';
import {IPaginationQuery, ISortQuery} from 'shared/types/requests';
import {omitEmptyLines, showMessage} from 'shared/utils';
import {EDeleteResponseSummaryStatus, showMessageFromResponse} from 'shared/utils/show-message-from-response';
import {useAppSelector} from 'store/config/hooks';

import {
    useDeleteAisSummaryTransactionProtocolTableRowsMutation,
    useFetchAisSummaryTransactionProtocolTableQuery,
    useUpdateAisSummaryTransactionProtocolTableRowsStatusMutation,
} from '../../api/api';
import {DEFAULT_TABLE_PAGE_SIZE} from '../../model/constants';
import {ISummaryTransactionProtocolFilters} from '../../model/types';
import {TableParametersFilter} from '../table-parameters-filter/table-parameters-filter';
import {TransactionProtocolTable} from '../transaction-protocol-table/transaction-protocol-table';

interface ISummaryTransactionsProtocolInterfaceProps {
      entityName: string;
}

export const SummaryTransactionsProtocolInterfacePage: FC<ISummaryTransactionsProtocolInterfaceProps> = ({
    entityName,
}: ISummaryTransactionsProtocolInterfaceProps) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [tableParametersFilter, setTableParametersFilter] = useState<ISummaryTransactionProtocolFilters>();
    const [isStatusChangingModalOpen, setIsStatusChangingModalOpen] = useState<boolean>(false);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false);
    const [sortQuery, setSortQuery] = useState<ISortQuery | undefined>(undefined);
    const [paginationQuery, setPaginationQuery] = useState<IPaginationQuery | undefined>({
        paginationCurrent: 1,
        paginationPageSize: DEFAULT_TABLE_PAGE_SIZE,
    });
    const tableRef = useRef<HTMLDivElement>(null);
    const {organizationId, taxPeriodId} = useAppSelector(selectContextRawData) ?? {};

    const parametersFilterRef = useRef<IParametersFilterRef<ISummaryTransactionProtocolFilters>>(null);

    const {
        data: tableDataResponse,
        isFetching: isTableLoading,
        refetch: refetchTableData,
    } = useFetchAisSummaryTransactionProtocolTableQuery({
        ...tableParametersFilter,
        ...sortQuery,
        ...paginationQuery,
    });

    const [updateAisSummaryTransactionProtocolTableRowsStatus, {
        isLoading: isStatusUpdating,
    }] = useUpdateAisSummaryTransactionProtocolTableRowsStatusMutation();

    const [deleteAisSummaryTransactionProtocolTableRows, {
        isLoading: isDeleting,
    }] = useDeleteAisSummaryTransactionProtocolTableRowsMutation();

    const handleStatusChange = async () => {
        try {
            await updateAisSummaryTransactionProtocolTableRowsStatus({
                ids: selectedRowKeys as number[],
            }).unwrap();
            showMessage({message: 'Статус записей был успешно изменен'});
            refetchTableData();
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Ошибка при изменении статуса записей: ', error);
        } finally {
            setIsStatusChangingModalOpen(p => !p);
        }
    };

    const handleDelete = async () => {
        try {
            const response = await deleteAisSummaryTransactionProtocolTableRows({
                ids: selectedRowKeys as number[],
            }).unwrap();
            showMessageFromResponse({data: response});
            if (response?.summaryStatus === EDeleteResponseSummaryStatus.ERROR) {
                throw new Error('В ответе от сервера summaryStatus = ERROR');
            }
            refetchTableData();
            setSelectedRowKeys([]);
        } catch (error) {
            console.error('Ошибка при удалении записей: ', error);
        } finally {
            setIsDeleteConfirmationModalOpen(p => !p);
        }
    };

    useEffect(()=>{
        refetchTableData();
    }, [organizationId, taxPeriodId]);

    return (
        <div>
            <CustomCard
                accordionTransitionType="1"
                isAccordionContentVisibleByDefault
                title="Открытый интерфейс Сводный протокол операций (АИС Налог-3)"
                accordionContent={(
                    <ParametersFilter<ISummaryTransactionProtocolFilters>
                        ref={parametersFilterRef}
                        onChange={values => {
                            setTableParametersFilter(omitEmptyLines(values));
                        }}
                    >
                        {TableParametersFilter}
                    </ParametersFilter>
                )}
            >
                <ActionBar>
                    <KebabMenu>
                        <Menu>
                            <Menu.Item
                                disabled={isTableLoading}
                            >
                                <TableLaunchProgramModalAction
                                    entityName={entityName}
                                    initialProgram="PUBL_SUMMARY_TRANSACTION_PROTOCOL"
                                    modalOpenerTriggerComponent={(<>Опубликовать</>)}
                                />
                            </Menu.Item>

                            <Menu.Item
                                disabled={!selectedRowKeys.length}
                                onClick={() => setIsStatusChangingModalOpen(p => !p)}
                            >
                                Изменить статус записи
                            </Menu.Item>

                            <Menu.Item
                                disabled={!selectedRowKeys.length}
                                onClick={() => setIsDeleteConfirmationModalOpen(p => !p)}
                            >
                                Удалить
                            </Menu.Item>

                            <ModalOpenerComponent
                                isOpen={isStatusChangingModalOpen}
                                setIsOpen={setIsStatusChangingModalOpen}
                                modalProps={{
                                    forceRender: false,
                                    destroyOnClose: true,
                                    title: 'Предупреждение',
                                    centered: true,
                                }}
                                controlLabels={{
                                    save: 'Да',
                                    cancel: 'Нет',
                                }}
                                loadingControls={{
                                    save: isStatusUpdating,
                                }}
                                disabledControls={{
                                    save: isStatusUpdating,
                                }}
                                handleSave={handleStatusChange}
                            >
                                Вы уверены, что хотите изменить статус выбранных записей?
                            </ModalOpenerComponent>

                            <ModalOpenerComponent
                                isOpen={isDeleteConfirmationModalOpen}
                                setIsOpen={setIsDeleteConfirmationModalOpen}
                                modalProps={{
                                    forceRender: false,
                                    destroyOnClose: true,
                                    title: 'Предупреждение',
                                    centered: true,
                                }}
                                controlLabels={{
                                    save: 'Да',
                                    cancel: 'Нет',
                                }}
                                loadingControls={{
                                    save: isDeleting,
                                }}
                                disabledControls={{
                                    save: isDeleting,
                                }}
                                handleSave={handleDelete}
                            >
                                Вы уверены, что хотите удалить выбранные записи?
                            </ModalOpenerComponent>
                        </Menu>
                    </KebabMenu>

                    <Button
                        disabled={isTableLoading}
                        icon={<ReloadOutlined />}
                        type="default"
                        onClick={refetchTableData}
                    >
                        Обновить
                    </Button>
                </ActionBar>

                <Spin
                    spinning={isTableLoading}
                    tip="Загрузка данных сводного протокола операций..."
                >
                    <TransactionProtocolTable
                        dataSource={tableDataResponse?.dataTable ?? []}
                        recordsTotal={tableDataResponse?.recordsTotal ?? 0}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        setSortQuery={setSortQuery}
                        setPaginationQuery={setPaginationQuery}
                        refetchTableData={refetchTableData}
                        tableRef={tableRef}
                    />
                    <HorizontalScroll
                        containerRef={tableRef}
                        scrollableSelector={CONTAINER_SELECTORS.TABLE}
                        enabled={tableDataResponse && tableDataResponse?.dataTable?.length > 0}
                    />
                </Spin>
            </CustomCard>
        </div>
    );
};
